import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { Checkbox, Chip, Radio, Tooltip, Typography, useTheme } from '@mui/material'
import { Anchor, Copy, Eye, EyeOff, Zap } from 'react-feather'
import { truncateOnOverflow } from 'src/lib/utils/helpers'
import GenericTable from 'src/components/common/table/GenericTable'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useMerchant } from 'src/lib/contexts/MerchantContext'

interface CustomizableDiscountsTableProps {
  page: number | undefined
  totalRowCount: number
  callGetAllDiscounts: (newPage: number) => Promise<void>
  rows: Array<any>
  handleChangePage: any
  setSelectedDiscountIds: (selectedDiscountIds: string[]) => void | React.Dispatch<React.SetStateAction<string[]>>
  selectedDiscountIds: Array<string>
  isLoading: boolean
  selectedDiscounts?: Array<any>
  setSelectedDiscounts?: React.Dispatch<React.SetStateAction<any[]>>
  isOnboarding?: boolean
  hideActive?: boolean
  pagination: {
    paginationState: PaginationState
    setPaginationState: Dispatch<SetStateAction<PaginationState>>
  }
  maxSelectableDiscounts?: number
  enabledInactive?: boolean
}

const CustomizableDiscountsTable: React.FC<CustomizableDiscountsTableProps> = ({
  page,
  totalRowCount,
  callGetAllDiscounts,
  rows,
  handleChangePage,
  setSelectedDiscountIds,
  selectedDiscountIds,
  isLoading,
  selectedDiscounts,
  setSelectedDiscounts,
  isOnboarding = false,
  hideActive,
  pagination,
  maxSelectableDiscounts,
  enabledInactive = false
}) => {
  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()

  useEffect(() => {
    if (!callGetAllDiscounts && !merchant) return
    callGetAllDiscounts(0)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // const [pagination, setPagination] = useState<PaginationState>({
  //   pageIndex: 0,
  //   pageSize: 10
  // })
  // const newrowsPerPage = pagination.pageSize
  // const newpage = pagination.pageIndex

  // Deleting the discount if it already exists in the list other wise pushing the discount into the list.
  const discountSelection = (row: any) => {
    const newSelectedDiscountIds = [...selectedDiscountIds]
    const newDiscounts = [...(selectedDiscounts ?? [])]

    if (selectedDiscountIds.includes(row?.couponId)) {
      setSelectedDiscountIds(newSelectedDiscountIds.filter((item: string) => item !== row?.couponId))
      setSelectedDiscounts && setSelectedDiscounts(newDiscounts.filter((item: any) => item?.couponId !== row?.couponId))
    } else {
      if (maxSelectableDiscounts === 1) {
        setSelectedDiscountIds([row?.couponId])
        setSelectedDiscounts && setSelectedDiscounts([row])
      } else {
        setSelectedDiscountIds([...newSelectedDiscountIds, row?.couponId])
        setSelectedDiscounts && setSelectedDiscounts([...newDiscounts, row])
      }
    }
  }

  const backgroundMapper = (value: string) => {
    switch (value) {
      case 'Scheduled':
        return `${theme.palette.warning.dark}1A`
      case 'Expired':
        return `${theme.palette.error.dark}1A`
      case 'Active':
        return `${theme.palette.info.dark}1A`
      case 'Inactive':
        return '#F5f5f5'
      case 'imported':
        return '#F5f5f5'
      case 'combines':
        return '#8A89DF1A'
      case 'automatic':
        return `${theme.palette.success.main}1A`
      default:
        return '#F5f5f5'
    }
  }
  const textColorMapper = (value: string) => {
    switch (value) {
      case 'Scheduled':
        return theme.palette.warning.dark
      case 'Expired':
        return theme.palette.error.dark
      case 'Active':
        return theme.palette.info.dark
      case 'Inactive':
        return theme.palette.text.secondary
      case 'imported':
        return theme.palette.text.secondary
      case 'combines':
        return '#4B4A9C'
      case 'automatic':
        return theme.palette.success.main
      default:
        return '#F5f5f5'
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      id: 'discoverable',
      minSize: 40,
      header: '',
      maxSize: 40,
      cell: ({ row }) => {
        const checked = Boolean(
          selectedDiscountIds?.findIndex((discountId: string) => discountId === row?.original.couponId) > -1
        )

        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {maxSelectableDiscounts === 1 ? (
              <Radio
                checked={checked}
                disabled={Boolean(
                  row.original['status'] === 'Expired' ||
                    Boolean(row.original['status'] === 'Inactive' && !enabledInactive) ||
                    (!isOnboarding && Boolean(row.original['automatic']))
                )}
                onClick={event => {
                  event.stopPropagation()
                  discountSelection(row.original)
                }}
                inputProps={{ 'aria-label': 'controlled' }}
                sx={{ padding: 0, color: 'grey' }}
              />
            ) : (
              <Checkbox
                checked={checked}
                disabled={Boolean(
                  row.original['status'] === 'Expired' ||
                    Boolean(row.original['status'] === 'Inactive' && !enabledInactive) ||
                    (!isOnboarding && Boolean(row.original['automatic'])) ||
                    (maxSelectableDiscounts && selectedDiscountIds.length >= maxSelectableDiscounts && !checked)
                )}
                onClick={event => {
                  event.stopPropagation()
                  discountSelection(row.original)
                }}
                inputProps={{ 'aria-label': 'controlled' }}
                sx={{ padding: 0, color: 'grey' }}
              />
            )}
          </div>
        )
      }
    },
    {
      id: 'code',
      header: () => <div style={{ marginLeft: '3px' }}>Code</div>,
      cell: ({ row }) => {
        const value = row.original['code']
        const hasTags = Boolean(row.original['imported']) || row.original['combines'] || row.original['automatic']
        const renderTags = () => (
          <>
            {row.original['imported'] && (
              <Chip
                size='small'
                avatar={<Copy style={{ height: 12, width: 12, color: textColorMapper('imported') }} />}
                sx={{
                  color: textColorMapper('imported'),
                  backgroundColor: backgroundMapper('imported'),
                  fontWeight: 500,
                  fontSize: 12,
                  borderRadius: 1,
                  height: 20,
                  marginLeft: '8px'
                }}
                label='Shopify import'
              />
            )}
            {row.original['combines'] && (
              <Chip
                size='small'
                avatar={<Anchor style={{ height: 12, width: 12, color: textColorMapper('combines') }} />}
                sx={{
                  color: textColorMapper('combines'),
                  backgroundColor: backgroundMapper('combines'),
                  fontWeight: 500,
                  fontSize: 12,
                  borderRadius: 1,
                  height: 20,
                  marginLeft: '8px'
                }}
                label='Combines'
              />
            )}
            {row.original['automatic'] && (
              <Chip
                size='small'
                avatar={<Zap style={{ height: 12, width: 12, color: textColorMapper('automatic') }} />}
                sx={{
                  color: textColorMapper('automatic'),
                  backgroundColor: backgroundMapper('automatic'),
                  fontWeight: 500,
                  fontSize: 12,
                  borderRadius: 1,
                  height: 20,
                  marginLeft: '8px'
                }}
                label='Automatic'
              />
            )}
          </>
        )

        return (
          <div style={{ display: 'flex', flexDirection: 'column', fontWeight: 500 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <Tooltip title={value.toUpperCase()}>
                <span
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#4d4d4d',
                    whiteSpace: 'nowrap',
                    maxWidth: hasTags ? '140px' : '240px'
                  }}
                >
                  {value.toUpperCase()}
                </span>
              </Tooltip>
              {renderTags()}
            </div>
            <Typography
              variant='body2'
              sx={{
                fontSize: '12px',
                color: '#949494',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                maxWidth: '240px'
              }}
            >
              {row?.original.descriptionTitle}
            </Typography>
          </div>
        )
      }
    },
    ...(!Boolean(hideActive)
      ? [
          {
            id: 'status',
            header: () => <div style={{ marginLeft: '4px' }}>Status</div>,
            minSize: 30,
            maxSize: 30,
            cell: ({ row }: { row: any }) => {
              const value = row.original['status']
              const isImported = row.original['imported']
              return isImported ? (
                <Typography variant='subtitle1' sx={{ color: 'text.secondary' }}>
                  -
                </Typography>
              ) : (
                <Chip
                  size='small'
                  sx={{
                    color: textColorMapper(value),
                    backgroundColor: backgroundMapper(value),
                    borderRadius: 1,
                    height: 24
                  }}
                  label={value}
                />
              )
            }
          }
        ]
      : []),
    {
      id: 'visible',
      header: () => <div style={{ marginLeft: '15px' }}>{t('visible_to_customer')}</div>,
      minSize: 100,
      maxSize: 140,
      cell: ({ row }: { row: any }) => {
        const value = row.original['discoverable']
        return (
          <div className='flex w-full justify-center items-center'>
            <div
              className={`px-1 py-[1px] rounded-md ${
                Boolean(value) ? 'text-[#C7981F] bg-[#EFD08033]' : 'text-gray-dark bg-gray-light'
              }`}
            >
              {Boolean(value) ? (
                <span className='inline-flex gap-0.5 items-center justify-center'>
                  <Eye className='w-4 h-4' />
                  <span>Yes</span>
                </span>
              ) : (
                <span className='inline-flex gap-0.5 items-center justify-center'>
                  <EyeOff className='w-4 h-4' />
                  <span>No</span>
                </span>
              )}
            </div>
          </div>
        )
      }
    }
  ]

  const theme = useTheme()

  return (
    <GenericTable
      data={rows}
      columns={columns}
      pagination={pagination}
      totalRows={totalRowCount}
      loading={isLoading}
      customPaginationConfig={{
        customPagination: false,
        customPageSizeOptionsForDropdown: [{ value: 10, label: '15' }]
      }}
      isCustomizable={{
        isFullWidth: false,
        isResizable: false,
        hideDropDown: true
      }}
    />
  )
}

export default CustomizableDiscountsTable
